import { useCallback, useEffect, useState } from 'react';
import { useLens } from '@hookform/lenses';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link } from '@tanstack/react-router';
import { FormProvider, type Path, useForm, useWatch } from 'react-hook-form';
import { match, P } from 'ts-pattern';
import { z } from 'zod/v4';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import { useActiveProgramId } from '@interxfi/api-client/context';
import { FormLayout } from '@interxfi/app-components/components/layout/action-layout/form-layout/form-layout';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { StatusBar } from '@interxfi/design-system/components/status-bar/status-bar';
import { TableOfContentsContextProvider } from '@interxfi/design-system/components/table-of-contents/table-of-contents-context';
import { useAddToast } from '@interxfi/design-system/components/toast/toast-context';
import AIIcon from '@interxfi/design-system/icons/react/icon-ai';
import UndoIcon from '@interxfi/design-system/icons/react/icon-undo';
import { useBreakpoint } from '@interxfi/design-system/util/responsive';
import { clone } from '@interxfi/utilities/object/clone';
import { set } from '@interxfi/utilities/object/set';
import { CurrentRealmProgramComboboxWithLens } from '../../../../../../../../../components/data-combobox-input/entity-config/program';
import { defaultIndividualProfileFormValues } from '../../../../../../../../../features/registration/profile/form/individual/individual-profile-form';
import { getFakeIndividualProfileFormValues } from '../../../../../../../../../features/registration/profile/form/individual/individual-profile-form-fake-values';
import { defaultOrganizationProfileFormValues } from '../../../../../../../../../features/registration/profile/form/organization/organization-profile-form';
import { getFakeOrganizationProfileFormValues } from '../../../../../../../../../features/registration/profile/form/organization/organization-profile-form-fake-values';
import {
  useProfileFormSubmissionContext,
  useResetProfileFormSubmissionContextOnNavigateAway,
  useSetInProgressProfile,
  useSetPendingProfile,
} from '../../../../../../../../../features/registration/profile/form/profile-form-submission-context';
import {
  formValuesToRegistrationProfileType,
  RegistrationProfileForm,
  registrationProfileFormSchema,
  registrationProfileOnSubmitFormSchema,
} from '../../../../../../../../../features/registration/profile/form/registration-profile-form';
import { RegistrationProfileFormTableOfContents } from '../../../../../../../../../features/registration/profile/form/registration-profile-form-table-of-contents';
import {
  getRegistrationProfileCreatePath,
  REGISTRATION_PROFILE_BASE_PATH,
} from '../../../../../../../../../features/registration/profile/registration-profile-routing';

const createProfileFormSchema = z.object({
  programId: z.string().nonempty('Program is required'),
  spec: registrationProfileFormSchema,
});

const createProfileOnSubmitFormSchema = createProfileFormSchema.safeExtend({
  spec: registrationProfileOnSubmitFormSchema,
});

type CreateProfileFormValues = z.infer<typeof createProfileFormSchema>;

interface WithDefaultValues {
  defaultValues: CreateProfileFormValues;
}

export const Route = createFileRoute('/_app/_clearing-core/registration/profile/_profile/create/$profileType/_create/')({
  component: CreateProfile,
  loader: ({ params }) => {
    const { profileType } = params;
    const { consumeInProgressProfile } = useProfileFormSubmissionContext.getState();

    const inProgressProfile = consumeInProgressProfile(profileType);

    const defaultValues = match(inProgressProfile)
      .with({ individual: P.not(P.nullish) }, ({ programId, individual }) => ({ programId, spec: individual }))
      .with({ organization: P.not(P.nullish) }, ({ programId, organization }) => ({ programId, spec: organization }))
      .otherwise(() => ({
        programId: '',
        spec: profileType === 'organization' ? defaultOrganizationProfileFormValues : defaultIndividualProfileFormValues,
      }));

    return { defaultValues } satisfies WithDefaultValues;
  },
  shouldReload: true,
  preload: false,
});

const rightColumnContainer = {
  width: { type: 'fixed', width: 320 },
} as const;

function CreateProfile() {
  const { profileType } = Route.useParams();
  const { defaultValues } = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const addToast = useAddToast();
  useResetProfileFormSubmissionContextOnNavigateAway(getRegistrationProfileCreatePath(profileType));
  const setInProgressProfile = useSetInProgressProfile();
  const setPendingProfile = useSetPendingProfile();
  const programId = useActiveProgramId();
  const [dataPriorToSeed, setDataPriorToSeed] = useState<CreateProfileFormValues | null>(null);
  const isTooCompactForTableOfContents = useBreakpoint('sm');

  const form = useForm<CreateProfileFormValues>({
    mode: 'all',
    defaultValues: {
      ...defaultValues,
      programId: programId ?? defaultValues.programId,
    },
    resolver: zodResolver(createProfileFormSchema),
  });
  const {
    control,
    formState: { isValid },
    getValues,
    handleSubmit,
    reset,
    setError,
    setValue,
  } = form;

  const { activeRealmIsSimulated } = useActiveRealmAPIContext();
  const selectedProgramId = useWatch({ control, name: 'programId' });
  const selectedProfileType = useWatch({ control, name: 'spec.type' });

  const populateSampleData = useCallback(async () => {
    const seedData = await match(selectedProfileType)
      .with('individual', () => getFakeIndividualProfileFormValues())
      .with('organization', () => getFakeOrganizationProfileFormValues())
      .otherwise(() => undefined);

    if (seedData) {
      const priorValues = getValues();
      setDataPriorToSeed(clone(priorValues));
      reset(set(priorValues, 'spec', seedData, true), { keepDirty: false, keepTouched: false, keepDefaultValues: false });
    }
  }, [reset, getValues, selectedProfileType]);

  const handleUndoSeedData = useCallback(() => {
    if (dataPriorToSeed) {
      reset(dataPriorToSeed, { keepDirty: false, keepTouched: false, keepDefaultValues: false });
      setDataPriorToSeed(null);
    }
  }, [dataPriorToSeed, reset]);

  useEffect(() => {
    if (programId) {
      setValue('programId', programId, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    }
  }, [programId, setValue]);

  useEffect(() => {
    // @ts-expect-error - for dev console usage only
    window.populateProfile = populateSampleData;

    return () => {
      // @ts-expect-error - for dev console usage only
      window.populateProfile = undefined;
    };
  }, [populateSampleData]);

  const seedDataBannerElement = activeRealmIsSimulated ? (
    <StatusBar
      actions={
        dataPriorToSeed
          ? [
              {
                id: 'undo',
                triggerStyle: 'button',
                ctaText: 'Undo',
                ctaIcon: <UndoIcon aria-hidden />,
                type: 'button',
                button: {
                  onClick: handleUndoSeedData,
                },
              },
            ]
          : [
              {
                id: 'populate',
                triggerStyle: 'button',
                ctaText: 'Populate',
                type: 'button',
                button: {
                  onClick: populateSampleData,
                },
              },
            ]
      }
      icon={<AIIcon aria-hidden width={16} />}
      title="Populate this form with realistic profile data?"
      variant="primary"
    />
  ) : null;

  const lens = useLens({ control });

  const onSubmit = useCallback(
    async (values: CreateProfileFormValues) => {
      const { success, error } = createProfileOnSubmitFormSchema.safeParse(values);
      if (!success) {
        error.issues?.forEach((issue) => {
          setError(issue.path.join('.') as Path<CreateProfileFormValues>, { message: issue.message });
        });

        addToast({
          id: 'createProfileError',
          variant: 'error',
          content: error.issues?.[0]?.message || 'There is a problem with the data you provided, please try again.',
        });

        return;
      }

      setInProgressProfile(
        match(values)
          .with({ spec: { type: 'individual' } }, (v) => ({ programId: v.programId, individual: v.spec }))
          .with({ spec: { type: 'organization' } }, (v) => ({ programId: v.programId, organization: v.spec }))
          .exhaustive(),
      );

      setPendingProfile({ programId: values.programId, spec: formValuesToRegistrationProfileType(values.spec) });

      await navigate({
        to: `/registration/profile/create/$profileType/review`,
        params: { profileType: values.spec.type },
      });
    },
    [navigate, setPendingProfile, setInProgressProfile, addToast, setError],
  );

  const baseFormElements = (
    <VerticalStack gap={7}>
      {!programId ? <CurrentRealmProgramComboboxWithLens label="Program" lens={lens.focus('programId')} required /> : null}

      <RegistrationProfileForm lens={lens.focus('spec')} populateSampleDataBannerElement={seedDataBannerElement} programId={selectedProgramId} />
    </VerticalStack>
  );

  return (
    <TableOfContentsContextProvider>
      <FormProvider {...form}>
        <FormLayout
          actions={[
            { buttonLink: { id: 'cancel', children: 'Cancel', LinkElement: Link, to: REGISTRATION_PROFILE_BASE_PATH, variant: 'secondary' } },
            { button: { id: 'continue', children: 'Continue', disabled: !isValid, type: 'submit', variant: 'primary' } },
          ]}
          maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}
          onSubmit={handleSubmit(onSubmit)}
        >
          {isTooCompactForTableOfContents ? (
            baseFormElements
          ) : (
            <WrappingLayoutContainer
              gap={DEFAULT_PAGE_LAYOUT_GAP}
              left={{
                ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
                render: baseFormElements,
              }}
              padding={LAYOUT_ZERO_CONTAINER_PADDING}
              right={{
                ...rightColumnContainer,
                render: (isWrapping) => (isWrapping ? null : <RegistrationProfileFormTableOfContents selectedProfileType={selectedProfileType} />),
              }}
              wrap="forward"
            />
          )}
        </FormLayout>
      </FormProvider>
    </TableOfContentsContextProvider>
  );
}
